// Empresas, Corporate astrological analysis services
// Routes:
//   /empresas        → public landing page
//   /empresas/equipe → team management (authenticated)
//   /empresas/socios → partner analysis (authenticated)

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Routes, Route } from 'react-router-dom';
import {
  Users, Plus, Trash2, Sparkles, ChevronRight,
  Building2, Handshake,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { safeInvoke } from '../lib/safeInvoke';
import { usePricing } from '../contexts/PricingContext';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

interface TeamMemberForm {
  name:       string;
  role:       string;
  birth_date: string;
  birth_time: string;
  birth_city: string;
  is_leader:  boolean;
}

interface Team {
  id:          string;
  team_name:   string;
  description: string | null;
}

interface TeamAnalysis {
  id:                      string;
  team_overview:           string;
  team_strengths:          string[];
  team_challenges:         string[];
  team_recommendations:    string;
  leader_member_synastries: PairSynastry[];
  member_synastries:        PairSynastry[];
  team_cohesion_score:      number;
  communication_score:      number;
  leadership_alignment:     number;
}

interface PairSynastry {
  person1:    string;
  person2:    string;
  score:      number;
  strengths:  string[];
  tensions:   string[];
  best_roles: string;
}

interface PartnerForm {
  name:       string;
  birth_date: string;
  birth_time: string;
}

interface PartnerAnalysisResult {
  id:                      string;
  compatibility_score:     number;
  business_strengths:      string[];
  business_challenges:     string[];
  role_suggestions:        Array<{ partner: string; ideal_role: string; reason: string }>;
  timing_recommendations:  string;
  full_analysis:           string;
  partner1_name:           string;
  partner2_name:           string;
}

// ─────────────────────────────────────────
// Score bar
// ─────────────────────────────────────────

function ScoreBar({ label, score }: { label: string; score: number }) {
  const color = score >= 75 ? '#4ade80' : score >= 50 ? '#fbbf24' : '#f87171';
  return (
    <div className="score-bar-container">
      <div className="score-bar-label">
        <span>{label}</span>
        <span style={{ color }}>{score}%</span>
      </div>
      <div className="score-bar-track">
        <div className="score-bar-fill" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Landing page
// ─────────────────────────────────────────

function EmpresasLanding() {
  const navigate = useNavigate();
  const { formatProductPrice, loading: pricingLoading } = usePricing();

  return (
    <div className="empresas-landing">
      <div className="empresas-hero">
        <div className="empresas-hero-icon">🏢</div>
        <h1 className="empresas-hero-title">
          Astrologia para Decisões<br />Empresariais Inteligentes
        </h1>
        <p className="empresas-hero-subtitle">
          Compreenda as dinâmicas energéticas da sua equipe e parceria antes de tomar decisões importantes.
        </p>
      </div>

      <div className="empresas-products-grid">
        {/* Team analysis card */}
        <div className="empresas-product-card">
          <div className="product-card-icon"><Users size={32} /></div>
          <h2 className="product-card-title">Análise de Equipe</h2>
          <p className="product-card-desc">
            Descubra a dinâmica astrológica da sua equipe, sinergias, tensões e o potencial coletivo.
          </p>
          <ul className="product-card-features">
            <li>Análise gestor × cada membro</li>
            <li>Compatibilidade entre todos os membros</li>
            <li>Recomendações de alocação de tarefas</li>
            <li>Pontos de tensão e harmonia do grupo</li>
          </ul>
          <div className="product-card-price">
            <span className="price-amount">{pricingLoading ? '...' : formatProductPrice('corporate_team_member')}</span>
            <span className="price-per">por membro analisado</span>
          </div>
          <button
            className="btn-primary product-card-cta"
            onClick={() => navigate('/empresas/equipe')}
          >
            <Building2 size={18} />
            Montar minha equipe
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Partner analysis card */}
        <div className="empresas-product-card featured">
          <div className="product-card-badge">Mais popular</div>
          <div className="product-card-icon"><Handshake size={32} /></div>
          <h2 className="product-card-title">Mapa de Sócios</h2>
          <p className="product-card-desc">
            Antes de assinar o contrato, consulte os astros. Entenda a compatibilidade da sua futura parceria.
          </p>
          <ul className="product-card-features">
            <li>Análise de compatibilidade dos mapas natais</li>
            <li>Perfis complementares vs conflitantes</li>
            <li>Sugestões de divisão de papéis</li>
            <li>Timing astrológico para decisões</li>
          </ul>
          <div className="product-card-price">
            <span className="price-amount">{pricingLoading ? '...' : formatProductPrice('corporate_partners')}</span>
            <span className="price-per">análise completa dos dois sócios</span>
          </div>
          <button
            className="btn-primary product-card-cta"
            onClick={() => navigate('/empresas/socios')}
          >
            <Handshake size={18} />
            Analisar nossa sociedade
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Team analysis page
// ─────────────────────────────────────────

const EMPTY_MEMBER: TeamMemberForm = {
  name: '', role: '', birth_date: '', birth_time: '', birth_city: '', is_leader: false,
};

function TeamPage() {
  const { profile } = useAuth();
  const { formatProductPrice, getPrice, loading: pricingLoading } = usePricing();
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamName,     setTeamName]     = useState('');
  const [members,      setMembers]      = useState<TeamMemberForm[]>([{ ...EMPTY_MEMBER, is_leader: true }]);
  const [analysis,     setAnalysis]     = useState<TeamAnalysis | null>(null);
  const [generating,   setGenerating]   = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [activeTab,    setActiveTab]    = useState<'overview' | 'pairs'>('overview');


  const addMember = () => {
    setMembers(prev => [...prev, { ...EMPTY_MEMBER }]);
  };

  const removeMember = (i: number) => {
    setMembers(prev => prev.filter((_, idx) => idx !== i));
  };

  const updateMember = (i: number, field: keyof TeamMemberForm, value: string | boolean) => {
    setMembers(prev => prev.map((m, idx) => idx === i ? { ...m, [field]: value } : m));
  };

  const handleGenerate = useCallback(async () => {
    if (!profile?.id) return;
    const leader = members.find(m => m.is_leader);
    if (!leader) { setError('Defina um gestor marcando "É gestor" em um dos membros.'); return; }
    if (members.length < 2) { setError('Adicione ao menos 2 pessoas (gestor + 1 membro).'); return; }

    setGenerating(true);
    setError(null);

    // Create team if not exists
    let teamId = selectedTeam?.id;
    if (!teamId) {
      if (!(teamName ?? '').trim()) { setError('Informe o nome da equipe.'); setGenerating(false); return; }
      const { data: newTeam, error: teamErr } = await supabase
        .from('corporate_teams')
        .insert({ owner_id: profile.id, team_name: teamName })
        .select()
        .maybeSingle();
      if (teamErr || !newTeam) { setError('Erro ao criar equipe.'); setGenerating(false); return; }
      teamId = newTeam.id;
      setSelectedTeam(newTeam as Team);
    }

    // Insert members
    const { error: memberErr } = await supabase
      .from('corporate_team_members')
      .insert(members.map(m => ({
        team_id:    teamId!,
        name:       m.name,
        role:       m.role || null,
        birth_date: m.birth_date,
        birth_time: m.birth_time || null,
        birth_city: m.birth_city || null,
        is_leader:  m.is_leader,
      })));

    if (memberErr) { setError('Erro ao salvar membros.'); setGenerating(false); return; }

    // Generate analysis
    const result = await safeInvoke<{ analysis: TeamAnalysis }>(
      'corporate-team-analysis',
      { body: { team_id: teamId, regenerate: true }, timeout: 90_000, fallback: null as unknown as { analysis: TeamAnalysis } },
    );

    if (result && !result.fromFallback && result.data?.analysis) {
      setAnalysis(result.data.analysis);
    } else {
      setError('Não foi possível gerar a análise. Tente novamente.');
    }
    setGenerating(false);
  }, [profile?.id, members, selectedTeam, teamName]);

  if (analysis) {
    return (
      <div className="team-analysis-results">
        <h2>Análise da Equipe</h2>

        <div className="team-scores-grid">
          <ScoreBar label="Coesão da equipe"         score={analysis.team_cohesion_score} />
          <ScoreBar label="Comunicação"               score={analysis.communication_score} />
          <ScoreBar label="Alinhamento com liderança" score={analysis.leadership_alignment} />
        </div>

        <div className="analysis-tabs">
          {(['overview', 'pairs'] as const).map(tab => (
            <button
              key={tab}
              className={`report-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'overview' ? 'Visão Geral' : 'Análise de Pares'}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div>
            <div className="report-section">
              <h3>Visão Geral</h3>
              <p>{analysis.team_overview}</p>
            </div>
            <div className="strengths-challenges-grid">
              <div>
                <h3>Pontos Fortes</h3>
                <ul className="report-list">
                  {analysis.team_strengths?.map((s, i) => (
                    <li key={i}><span className="report-list-bullet">✓</span>{s}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3>Desafios</h3>
                <ul className="report-list">
                  {analysis.team_challenges?.map((c, i) => (
                    <li key={i}><span className="report-list-bullet">!</span>{c}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="report-section">
              <h3>Recomendações para o Gestor</h3>
              <p>{analysis.team_recommendations}</p>
            </div>
          </div>
        )}

        {activeTab === 'pairs' && (
          <div>
            {analysis.leader_member_synastries?.length > 0 && (
              <div className="report-section">
                <h3>Gestor × Membros</h3>
                {analysis.leader_member_synastries.map((pair, i) => (
                  <div key={i} className="pair-card">
                    <div className="pair-header">
                      <span>{pair.person1} ↔ {pair.person2}</span>
                      <ScoreBar label="Compatibilidade" score={pair.score} />
                    </div>
                    <p className="pair-best-roles">{pair.best_roles}</p>
                  </div>
                ))}
              </div>
            )}
            {analysis.member_synastries?.length > 0 && (
              <div className="report-section">
                <h3>Entre Membros</h3>
                {analysis.member_synastries.map((pair, i) => (
                  <div key={i} className="pair-card">
                    <div className="pair-header">
                      <span>{pair.person1} ↔ {pair.person2}</span>
                      <ScoreBar label="Compatibilidade" score={pair.score} />
                    </div>
                    <p className="pair-best-roles">{pair.best_roles}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <button className="btn-secondary" onClick={() => setAnalysis(null)} style={{ marginTop: '2rem' }}>
          Nova análise
        </button>
      </div>
    );
  }

  return (
    <div className="team-page">
      <h2>Análise de Equipe</h2>
      <p className="page-subtitle">
        Adicione o gestor e os membros da equipe. Cada membro custa {pricingLoading ? '...' : formatProductPrice('corporate_team_member')}.
      </p>

      {error && <div className="error-banner">{error}</div>}

      {/* Team name */}
      <div className="form-group">
        <label>Nome da equipe</label>
        <input
          type="text"
          className="form-input"
          value={teamName}
          onChange={e => setTeamName(e.target.value)}
          placeholder="ex: Time de Produto"
        />
      </div>

      {/* Members */}
      <div className="members-list">
        {members.map((member, i) => (
          <div key={i} className={`member-form ${member.is_leader ? 'leader' : ''}`}>
            <div className="member-form-header">
              <span className="member-form-label">
                {member.is_leader ? '👑 Gestor' : `Membro ${i}`}
              </span>
              {!member.is_leader && (
                <button className="remove-btn" onClick={() => removeMember(i)}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
            <div className="member-form-grid">
              <input type="text" className="form-input" placeholder="Nome *"
                value={member.name} onChange={e => updateMember(i, 'name', e.target.value)} />
              <input type="text" className="form-input" placeholder="Cargo / Função"
                value={member.role} onChange={e => updateMember(i, 'role', e.target.value)} />
              <input type="date" className="form-input"
                value={member.birth_date} onChange={e => updateMember(i, 'birth_date', e.target.value)} />
              <input type="time" className="form-input" placeholder="Hora de nascimento (opcional)"
                value={member.birth_time} onChange={e => updateMember(i, 'birth_time', e.target.value)} />
            </div>
            {!member.is_leader && (
              <label className="checkbox-label">
                <input type="checkbox" checked={member.is_leader}
                  onChange={e => updateMember(i, 'is_leader', e.target.checked)} />
                É gestor/líder
              </label>
            )}
          </div>
        ))}
      </div>

      <div className="team-form-actions">
        <button className="btn-secondary" onClick={addMember}>
          <Plus size={16} /> Adicionar membro
        </button>

        <div className="team-price-summary">
          <span>Total: R$ {(members.filter(m => !m.is_leader).length * getPrice('corporate_team_member')).toFixed(0)},00</span>
          <span className="price-note">({members.filter(m => !m.is_leader).length} membro(s) × {formatProductPrice('corporate_team_member')})</span>
        </div>

        <button
          className="btn-primary"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? (
            <><Sparkles size={16} className="animate-pulse" /> Analisando...</>
          ) : (
            <><Sparkles size={16} /> Gerar análise da equipe</>
          )}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Partner analysis page
// ─────────────────────────────────────────

function PartnersPage() {
  const { profile } = useAuth();
  const { formatProductPrice, loading: pricingLoading } = usePricing();
  const [partner1,   setPartner1]   = useState<PartnerForm>({ name: '', birth_date: '', birth_time: '' });
  const [partner2,   setPartner2]   = useState<PartnerForm>({ name: '', birth_date: '', birth_time: '' });
  const [analysis,   setAnalysis]   = useState<PartnerAnalysisResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [activeTab,  setActiveTab]  = useState<'overview' | 'roles' | 'timing'>('overview');

  // Load most recent analysis
  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from('partner_analyses')
      .select('*')
      .eq('owner_id', profile.id)
      .order('generated_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => { if (data) setAnalysis(data as PartnerAnalysisResult); });
  }, [profile?.id]);

  const handleGenerate = useCallback(async () => {
    if (!partner1.name || !partner1.birth_date) { setError('Preencha os dados do Sócio 1.'); return; }
    if (!partner2.name || !partner2.birth_date) { setError('Preencha os dados do Sócio 2.'); return; }

    setGenerating(true);
    setError(null);

    const result = await safeInvoke<{ analysis: PartnerAnalysisResult }>(
      'partner-analysis',
      {
        body: {
          partner1: { name: partner1.name, birth_date: partner1.birth_date, birth_time: partner1.birth_time || undefined },
          partner2: { name: partner2.name, birth_date: partner2.birth_date, birth_time: partner2.birth_time || undefined },
          regenerate: true,
        },
        timeout: 90_000,
        fallback: null as unknown as { analysis: PartnerAnalysisResult },
      },
    );

    if (result && !result.fromFallback && result.data?.analysis) {
      setAnalysis(result.data.analysis);
    } else {
      setError('Não foi possível gerar a análise. Tente novamente.');
    }
    setGenerating(false);
  }, [partner1, partner2]);

  if (analysis) {
    const scoreColor = analysis.compatibility_score >= 75 ? '#4ade80'
      : analysis.compatibility_score >= 50 ? '#fbbf24' : '#f87171';

    return (
      <div className="partner-results">
        <h2>Análise de Sociedade</h2>
        <p className="result-partners">
          {analysis.partner1_name} × {analysis.partner2_name}
        </p>

        <div className="compatibility-score-circle" style={{ borderColor: scoreColor }}>
          <span className="score-number" style={{ color: scoreColor }}>
            {analysis.compatibility_score}
          </span>
          <span className="score-label">Compatibilidade</span>
        </div>

        <div className="analysis-tabs">
          {(['overview', 'roles', 'timing'] as const).map(tab => (
            <button
              key={tab}
              className={`report-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'overview' ? 'Análise Geral' : tab === 'roles' ? 'Papéis Sugeridos' : 'Timing'}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div>
            <div className="report-section">
              <h3>Análise Completa</h3>
              <p>{analysis.full_analysis}</p>
            </div>
            <div className="strengths-challenges-grid">
              <div>
                <h3>Pontos Fortes da Parceria</h3>
                <ul className="report-list">
                  {analysis.business_strengths?.map((s, i) => (
                    <li key={i}><span className="report-list-bullet">✓</span>{s}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3>Pontos de Atenção</h3>
                <ul className="report-list">
                  {analysis.business_challenges?.map((c, i) => (
                    <li key={i}><span className="report-list-bullet">!</span>{c}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'roles' && (
          <div className="report-section">
            <h3>Sugestões de Papéis</h3>
            {analysis.role_suggestions?.map((r, i) => (
              <div key={i} className="role-card">
                <h4>{r.partner}</h4>
                <p className="role-title">{r.ideal_role}</p>
                <p>{r.reason}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'timing' && (
          <div className="report-section">
            <h3>Timing Astrológico</h3>
            <p>{analysis.timing_recommendations}</p>
          </div>
        )}

        <button className="btn-secondary" onClick={() => setAnalysis(null)} style={{ marginTop: '2rem' }}>
          Nova análise
        </button>
      </div>
    );
  }

  return (
    <div className="partners-page">
      <h2>Mapa de Sócios</h2>
      <p className="page-subtitle">
        Análise astrológica completa de compatibilidade empresarial, {pricingLoading ? '...' : formatProductPrice('corporate_partners')}
      </p>

      {error && <div className="error-banner">{error}</div>}

      <div className="partners-forms-grid">
        {/* Partner 1 */}
        <div className="partner-form-card">
          <h3>Sócio 1</h3>
          <div className="form-group">
            <label>Nome *</label>
            <input type="text" className="form-input" placeholder="Nome completo"
              value={partner1.name} onChange={e => setPartner1(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Data de nascimento *</label>
            <input type="date" className="form-input"
              value={partner1.birth_date} onChange={e => setPartner1(p => ({ ...p, birth_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Hora de nascimento (opcional)</label>
            <input type="time" className="form-input"
              value={partner1.birth_time} onChange={e => setPartner1(p => ({ ...p, birth_time: e.target.value }))} />
          </div>
        </div>

        {/* Partner 2 */}
        <div className="partner-form-card">
          <h3>Sócio 2</h3>
          <div className="form-group">
            <label>Nome *</label>
            <input type="text" className="form-input" placeholder="Nome completo"
              value={partner2.name} onChange={e => setPartner2(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Data de nascimento *</label>
            <input type="date" className="form-input"
              value={partner2.birth_date} onChange={e => setPartner2(p => ({ ...p, birth_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Hora de nascimento (opcional)</label>
            <input type="time" className="form-input"
              value={partner2.birth_time} onChange={e => setPartner2(p => ({ ...p, birth_time: e.target.value }))} />
          </div>
        </div>
      </div>

      <div className="partners-cta">
        <div className="partners-price">{pricingLoading ? '...' : formatProductPrice('corporate_partners')}, análise completa</div>
        <button className="btn-primary" onClick={handleGenerate} disabled={generating}>
          {generating ? (
            <><Sparkles size={16} className="animate-pulse" /> Analisando...</>
          ) : (
            <><Sparkles size={16} /> Analisar nossa sociedade</>
          )}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Root Empresas component with sub-routing
// ─────────────────────────────────────────

export function Empresas() {
  const navigate  = useNavigate();
  const location  = useLocation();

  const tabs = [
    { path: '/empresas',        label: 'Início' },
    { path: '/empresas/equipe', label: 'Análise de Equipe' },
    { path: '/empresas/socios', label: 'Mapa de Sócios' },
  ];

  return (
    <div className="page-container">
      {/* Tab nav */}
      <div className="empresas-tabs">
        {tabs.map(tab => (
          <button
            key={tab.path}
            className={`empresas-tab ${location.pathname === tab.path ? 'active' : ''}`}
            onClick={() => navigate(tab.path)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <Routes>
        <Route index element={<EmpresasLanding />} />
        <Route path="equipe" element={<TeamPage />} />
        <Route path="socios" element={<PartnersPage />} />
      </Routes>
    </div>
  );
}
